import { useState, useEffect } from 'react';
import { Container, Title, Grid, Card, Text, Group, Progress, Badge, Stack, Button } from '@mantine/core';
import { IconUsers, IconEye, IconClock, IconTrendingUp, IconRefresh } from '@tabler/icons-react';

interface VisitorStats {
  uniqueVisitors: number;
  totalSessions: number;
  totalPageViews: number;
  avgSessionDuration: number;
  topPages: Array<{ page: string; visits: number }>;
}

export default function VisitorAnalyticsPage() {
  const [stats, setStats] = useState<VisitorStats>({
    uniqueVisitors: 0,
    totalSessions: 0,
    totalPageViews: 0,
    avgSessionDuration: 0,
    topPages: [],
  });

  const loadStats = () => {
    try {
      // Get all sessions from localStorage
      const stored = localStorage.getItem('analytics_all_sessions');
      const sessions = stored ? JSON.parse(stored) : [];

      if (sessions.length === 0) {
        setStats({
          uniqueVisitors: 0,
          totalSessions: 0,
          totalPageViews: 0,
          avgSessionDuration: 0,
          topPages: [],
        });
        return;
      }

      // Calculate unique visitors
      const uniqueIPs = new Set(sessions.map((s: any) => s.ipAddress || s.sessionId));

      // Calculate total page views
      const totalPageViews = sessions.reduce((sum: number, s: any) => sum + s.pageVisits.length, 0);

      // Calculate average session duration
      const avgSessionDuration =
        sessions.reduce((sum: number, s: any) => {
          const start = new Date(s.startTime).getTime();
          const end = new Date(s.lastActivity).getTime();
          return sum + (end - start);
        }, 0) / sessions.length;

      // Get top pages
      const pageMap = new Map<string, number>();
      sessions.forEach((s: any) => {
        s.pageVisits.forEach((pv: any) => {
          pageMap.set(pv.page, (pageMap.get(pv.page) || 0) + 1);
        });
      });

      const topPages = Array.from(pageMap.entries())
        .map(([page, visits]) => ({ page, visits }))
        .sort((a, b) => b.visits - a.visits)
        .slice(0, 10);

      setStats({
        uniqueVisitors: uniqueIPs.size,
        totalSessions: sessions.length,
        totalPageViews,
        avgSessionDuration,
        topPages,
      });
    } catch (error) {
      console.error('Failed to load analytics:', error);
    }
  };

  useEffect(() => {
    loadStats();
    // Refresh every 30 seconds
    const interval = setInterval(loadStats, 30000);
    return () => clearInterval(interval);
  }, []);

  const formatDuration = (ms: number) => {
    const seconds = Math.floor(ms / 1000);
    if (seconds < 60) return `${seconds}s`;
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
    const hours = Math.floor(minutes / 60);
    return `${hours}h ${minutes % 60}m`;
  };

  const maxPageVisits = Math.max(...stats.topPages.map(p => p.visits), 1);

  return (
    <Container size="lg" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <div>
          <Group justify="space-between" align="flex-start">
            <div>
              <Title order={1}>Visitor Analytics</Title>
              <Text c="dimmed" size="sm" mt="xs">
                Real-time traffic and engagement metrics for rooke.analyticlabs.io
              </Text>
            </div>
            <Group gap="xs">
              <Badge color="green" variant="filled">
                🟢 Live
              </Badge>
              <Button
                size="xs"
                variant="light"
                onClick={loadStats}
                leftSection={<IconRefresh size={14} />}
              >
                Refresh
              </Button>
            </Group>
          </Group>
        </div>

        {/* Main Stats Grid */}
        <Grid>
          {/* Unique Visitors */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Unique Visitors
                </Text>
                <Badge color="blue" variant="light">
                  <IconUsers size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {stats.uniqueVisitors}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Distinct IP addresses / sessions
              </Text>
            </Card>
          </Grid.Col>

          {/* Total Sessions */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Total Sessions
                </Text>
                <Badge color="violet" variant="light">
                  <IconTrendingUp size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {stats.totalSessions}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Visitor sessions
              </Text>
            </Card>
          </Grid.Col>

          {/* Page Views */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Page Views
                </Text>
                <Badge color="orange" variant="light">
                  <IconEye size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {stats.totalPageViews}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Total page navigations
              </Text>
            </Card>
          </Grid.Col>

          {/* Avg Session Duration */}
          <Grid.Col span={{ base: 12, sm: 6, md: 3 }}>
            <Card withBorder padding="lg" radius="md">
              <Group justify="space-between" mb="md">
                <Text size="sm" fw={500} c="dimmed">
                  Avg Duration
                </Text>
                <Badge color="cyan" variant="light">
                  <IconClock size={14} />
                </Badge>
              </Group>
              <Text fw={700} size="xl">
                {formatDuration(stats.avgSessionDuration)}
              </Text>
              <Text size="xs" c="dimmed" mt="xs">
                Average session length
              </Text>
            </Card>
          </Grid.Col>
        </Grid>

        {/* Top Pages */}
        <Card withBorder padding="lg" radius="md">
          <Text fw={600} mb="md">
            Top Pages
          </Text>
          {stats.topPages.length === 0 ? (
            <Text c="dimmed" size="sm">
              No page visit data yet
            </Text>
          ) : (
            <Stack gap="sm">
              {stats.topPages.map((page, idx) => (
                <div key={idx}>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>
                      {page.page || '/'}
                    </Text>
                    <Text fw={600} size="sm">
                      {page.visits} views
                    </Text>
                  </Group>
                  <Progress
                    value={(page.visits / maxPageVisits) * 100}
                    color="blue"
                    size="sm"
                    mb="md"
                  />
                </div>
              ))}
            </Stack>
          )}
        </Card>

        {/* Info Box */}
        <Card withBorder padding="md" radius="md" style={{ background: '#f0f9ff' }}>
          <Group gap="xs">
            <Text size="sm" style={{ flex: 1 }}>
              💡 <strong>Tip:</strong> Analytics tracks visitor sessions, page views, and engagement metrics.
              Data is stored locally and persisted across sessions. Refresh to see latest stats.
            </Text>
          </Group>
        </Card>
      </Stack>
    </Container>
  );
}
